import React, { useState } from "react";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { VbButton, BIG5_LABELS, Input, type Big5Category } from "@/components/vb";
import type { Question, QuestionSource } from "@/types/vb";
import { MAX_PER_CATEGORY } from "./useBuyerWizard";

const SOURCE: Record<QuestionSource, { label: string; cls: string }> = {
  GENERATED: { label: "Generated", cls: "" },
  EDITED: { label: "Edited", cls: "edited" },
  MANUAL: { label: "Manual", cls: "manual" },
};

function QuestionItem({
  q,
  onEdit,
  onRemove,
}: {
  q: Question;
  onEdit: (id: string, text: string) => void;
  onRemove: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(q.text);

  const start = () => {
    setDraft(q.text);
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const save = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== q.text) onEdit(q.id, next);
  };

  const src = SOURCE[q.source];

  return (
    <div className={`q-item${editing ? " editing" : ""}`}>
      {editing ? (
        <input
          className="q-edit"
          value={draft}
          autoFocus
          maxLength={500}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              cancel();
            }
          }}
          aria-label="Edit question"
        />
      ) : (
        <span className="text">{q.text}</span>
      )}

      {!editing && <span className={`q-src ${src.cls}`}>{src.label}</span>}

      <span className="q-tools">
        {editing ? (
          <>
            <button type="button" className="q-save" onClick={save}>
              <Check size={13} aria-hidden="true" />
              Save
            </button>
            <button type="button" className="q-tool" title="Cancel" onClick={cancel} aria-label="Cancel edit">
              <X size={15} aria-hidden="true" />
            </button>
          </>
        ) : (
          <>
            <button type="button" className="q-tool" title="Edit" onClick={start} aria-label="Edit question">
              <Pencil size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="q-tool"
              title="Remove"
              onClick={() => onRemove(q.id)}
              aria-label="Remove question"
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
          </>
        )}
      </span>
    </div>
  );
}

type Props = {
  category: Big5Category;
  questions: Question[];
  onEdit: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  onAdd: (text: string) => void;
  /** A question add is in flight - disable the control to avoid double-POSTs. */
  busy?: boolean;
};

/** Per-category panel description (matches the aits-2.0 prototype's panel-hd),
 *  shown above each category's questions once they exist. */
const PANEL_DESC: Record<Big5Category, string> = {
  cost: "The first thing every buyer wants to know, and the question AI gets asked most often.",
  problems: "The doubts and objections this buyer raises before committing.",
  head_to_head: "Direct comparisons against the competitors you named.",
  reviews: "What this buyer hears about your reputation and track record.",
  best_of: 'The "best of" lists AI surfaces, and whether you show up on them.',
};

export function QuestionList({ category, questions, onEdit, onRemove, onAdd, busy }: Props) {
  const [draft, setDraft] = useState("");
  const atCap = questions.length >= MAX_PER_CATEGORY;
  const disabled = atCap || !!busy;

  const submit = () => {
    const value = draft.trim();
    if (!value || disabled) return;
    onAdd(value);
    setDraft("");
  };

  return (
    <div className="q-panel active">
      <div className="panel-hd">
        <div className="row">
          <h3>{BIG5_LABELS[category]}</h3>
          <span className="tag">Big 5 buyer topic</span>
        </div>
        <div className="desc">{PANEL_DESC[category]}</div>
      </div>

      <div className="qlist-hd">
        Questions <span className="of">· up to {MAX_PER_CATEGORY} per topic</span>
      </div>

      <div className="qlist">
        {questions.length === 0 ? (
          <div className="vb-cell-empty">No questions in this topic yet. Add one below.</div>
        ) : (
          questions.map((q) => (
            <QuestionItem key={q.id} q={q} onEdit={onEdit} onRemove={onRemove} />
          ))
        )}
      </div>

      <div className="own">
        <div className="lbl">Add your own</div>
        <div className="own-row">
          <Input
            size="lg"
            type="text"
            value={draft}
            placeholder="What would this buyer actually ask?"
            disabled={disabled}
            maxLength={500}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            aria-label="Add a question"
          />
          <VbButton type="button" variant="secondary" onClick={submit} disabled={disabled}>
            Add
          </VbButton>
        </div>
        <div className="own-meta">
          {atCap
            ? `This topic is full (${MAX_PER_CATEGORY}). Remove one to add another.`
            : `Adds a question to this topic. Up to ${MAX_PER_CATEGORY} per topic, 10 total.`}
        </div>
      </div>
    </div>
  );
}

export default QuestionList;
